import { type MedicationStatementDto, recordMedication, updateMedication } from "@repo/data-store";
import { asPatientId, DOSE_FREQUENCIES, DOSE_UNITS, MEDICATION_ROUTES, type MedicationDosage } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { type CodeValue, toCodeableConcept } from "@/shared/forms/code-value";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import { useSubmitOnce } from "@/shared/forms/submit-once";
import { dosageSentence, FREQUENCY_OPTIONS, ROUTE_OPTIONS, UNIT_OPTIONS } from "@/shared/lib/medication-dosage";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "intended", label: "Intended" },
  { value: "on-hold", label: "On hold" },
] as const;

const NONE = "__none__";

const medicationFormSchema = z.object({
  drug: z.custom<CodeValue | undefined>().refine((v) => Boolean(v?.code || v?.text?.trim()), "Choose a medication"),
  amount: z.string().refine((v) => v === "" || Number(v) > 0, "Enter an amount above 0"),
  unit: z.enum([NONE, ...DOSE_UNITS]),
  frequency: z.enum([NONE, ...DOSE_FREQUENCIES]),
  route: z.enum([NONE, ...MEDICATION_ROUTES]),
  asNeeded: z.boolean(),
  instructions: z.string(),
  status: z.enum(["active", "intended", "on-hold"]),
});
type MedicationFormValues = z.infer<typeof medicationFormSchema>;

function toDosage(
  v: Pick<MedicationFormValues, "amount" | "unit" | "frequency" | "route" | "asNeeded">,
): MedicationDosage {
  const value = Number(v.amount);
  return {
    ...(v.amount !== "" && value > 0 && v.unit !== NONE ? { amount: { value, unit: v.unit } } : {}),
    ...(v.frequency !== NONE ? { frequency: v.frequency } : {}),
    ...(v.route !== NONE ? { route: v.route } : {}),
    ...(v.asNeeded ? { asNeeded: true } : {}),
  };
}

const withNone = (options: { value: string; label: string }[]) => [{ value: NONE, label: "—" }, ...options];

export interface MedicationDialogProps {
  patientId: string;
  editing: MedicationStatementDto | null;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

export function MedicationDialog({ patientId, editing, open, onClose, onExited }: MedicationDialogProps) {
  const editingStatus = editing?.status;
  const defaultStatus =
    editingStatus === "intended" || editingStatus === "on-hold" ? editingStatus : ("active" as const);

  const stored = editing?.dosage;
  // The line is the dose sentence, then any instructions after a comma. Only the
  // instructions are shown, so the sentence keeps following the fields.
  const storedText = editing?.dosageText ?? "";
  const storedSentence = dosageSentence(stored);
  const customText =
    storedSentence && storedText === storedSentence
      ? ""
      : storedSentence && storedText.startsWith(`${storedSentence}, `)
        ? storedText.slice(storedSentence.length + 2)
        : storedText;

  const { once } = useSubmitOnce();
  const form = useAppForm({
    defaultValues: {
      drug: editing?.medication satisfies CodeValue | undefined,
      amount: stored?.amount ? String(stored.amount.value) : "",
      unit: stored?.amount?.unit ?? NONE,
      frequency: stored?.frequency ?? NONE,
      route: stored?.route ?? NONE,
      asNeeded: stored?.asNeeded ?? false,
      instructions: customText,
      status: defaultStatus,
    } as MedicationFormValues,
    validators: { onDynamic: medicationFormSchema },
    onSubmit: ({ value }) =>
      once((entityId) => {
        const dosage = toDosage(value);
        const sentence = dosageSentence(dosage);
        const instructions = value.instructions.trim();
        const dosageText = [sentence, instructions].filter(Boolean).join(", ") || undefined;
        if (editing) {
          updateMedication({
            medicationStatementId: editing.medicationStatementId,
            dosageText: dosageText ?? "",
            dosage,
          });
        } else if (patientId) {
          recordMedication({
            id: entityId,
            patient: { resourceType: "Patient", id: asPatientId(patientId) },
            medication: toCodeableConcept(value.drug as CodeValue),
            dosageText,
            ...(Object.keys(dosage).length ? { dosage } : {}),
            status: value.status,
          });
        }
        onClose();
      }),
    onSubmitInvalid: focusFirstInvalid,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onOpenChangeComplete={(next) => !next && onExited()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogTitle>{editing ? "Edit Medication" : "Record Medication"}</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="mt-2 space-y-4"
        >
          <form.AppField name="drug">
            {(field) => <field.MedicationField label="Medication" disabled={!!editing} />}
          </form.AppField>
          <div className="grid grid-cols-2 gap-3">
            <form.AppField name="amount">
              {(field) => <field.InputField label="Amount" placeholder="e.g. 1" inputMode="decimal" />}
            </form.AppField>
            <form.AppField name="unit">
              {(field) => <field.SelectField label="Unit" options={withNone(UNIT_OPTIONS)} />}
            </form.AppField>
            <form.AppField name="frequency">
              {(field) => <field.SelectField label="Frequency" options={withNone(FREQUENCY_OPTIONS)} />}
            </form.AppField>
            <form.AppField name="route">
              {(field) => <field.SelectField label="Route" options={withNone(ROUTE_OPTIONS)} />}
            </form.AppField>
          </div>
          <form.AppField name="asNeeded">{(field) => <field.SwitchField label="As needed (PRN)" />}</form.AppField>
          <form.Subscribe selector={(s) => dosageSentence(toDosage(s.values))}>
            {(sentence) => (
              <form.AppField name="instructions">
                {(field) => (
                  <field.InputField
                    label="Instructions"
                    description={sentence ? `Added after "${sentence}"` : undefined}
                    placeholder={sentence ? "e.g. take with food" : "e.g. 1 tablet by mouth daily"}
                  />
                )}
              </form.AppField>
            )}
          </form.Subscribe>
          {!editing && (
            <form.AppField name="status">
              {(field) => <field.OptionGroupField size="sm" label="Status" options={[...STATUS_OPTIONS]} />}
            </form.AppField>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <form.AppForm>
              <form.SubmitButton disabled={!editing && !patientId}>
                {editing ? "Save" : "Record Medication"}
              </form.SubmitButton>
            </form.AppForm>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
